import { notify } from '@/lib/toast';
import { useState, useMemo } from 'react';
import { RecordLink } from '@/components/records/RecordLink';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Plus, CheckCircle, Calendar } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { formatCurrency, formatDate, formatDateOnly } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { TabBar } from '@/components/ui/tab-bar';
import { FilterChips } from '@/components/ui/filter-chips';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EntityAutocomplete } from '@/components/ui/entity-autocomplete';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import toast from 'react-hot-toast';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface SaleOption {
  id: string;
  invoiceNumber: string;
  totalAmount: number | string;
  customer?: { name?: string | null } | null;
}

interface InstallmentPayment {
  id: string;
  dueDate: string;
  amount: number;
  paidAmount?: number | null;
  status: string;
  paidAt?: string | null;
}

interface InstallmentPlan {
  id: string;
  status: string;
  frequency: string;
  sale?: { id?: string; invoiceNumber?: string | null } | null;
  customer?: { name?: string | null } | null;
  totalAmount: number;
  amountPaid: number;
  remainingAmount: number;
  downPayment?: number | null;
  numberOfInstallments: number;
  paidInstallments: number;
  nextDueDate: string;
  payments?: InstallmentPayment[];
}

interface InstallmentPlanDetail extends InstallmentPlan {
  payments: InstallmentPayment[];
}

interface InstallmentListResponse {
  data?: InstallmentPlan[];
  total?: number;
}

type CreatePlanInput = Omit<ReturnType<typeof emptyForm>, 'numberOfInstallments' | 'downPayment'> & {
  numberOfInstallments: number;
  downPayment: number;
};

type PayInstallmentInput = ReturnType<typeof emptyPayForm> & { planId?: string };

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-info/10 text-info-text',
  COMPLETED: 'bg-success/10 text-success-text',
  DEFAULTED: 'bg-destructive/10 text-destructive-text',
  CANCELLED: 'bg-muted text-muted-foreground',
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-warning/10 text-warning-text',
  PAID: 'bg-success/10 text-success-text',
  OVERDUE: 'bg-destructive/10 text-destructive-text',
  WAIVED: 'bg-muted text-muted-foreground',
};

const emptyForm = () => ({
  saleId: '',
  downPayment: 0,
  downPaymentMethod: 'CASH',
  downPaymentBankAccountId: '',
  numberOfInstallments: 3,
  frequency: 'MONTHLY' as const,
  startDate: new Date().toISOString().split('T')[0],
  notes: '',
});

const emptyPayForm = () => ({
  paymentId: '',
  amount: 0,
  installmentDue: 0,
  paymentMethod: 'CASH',
  bankAccountId: '',
  notes: '',
});

type ViewTab = 'all' | 'overdue';

export function InstallmentsPage() {
  const { t } = useTranslation('installments');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('pos:create') || hasPermission('pos:*');
  const canCancel = hasPermission('pos:delete') || hasPermission('pos:*');
  const qc = useQueryClient();

  const [viewTab, setViewTab] = useState<ViewTab>('all');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [detailPlan, setDetailPlan] = useState<InstallmentPlan | null>(null);
  const [payPlan, setPayPlan] = useState<InstallmentPlan | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [selectedSale, setSelectedSale] = useState<SaleOption | null>(null);
  const [payForm, setPayForm] = useState(emptyPayForm());

  const planStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      ACTIVE: t('statusActive'),
      COMPLETED: t('statusCompleted'),
      DEFAULTED: t('statusDefaulted'),
      CANCELLED: t('statusCancelled'),
      DRAFT: t('statusDraft'),
    };
    return map[status] ?? status;
  };

  const statusFilterChips = useMemo(
    () => [
      { key: '', label: t('filterAll') },
      { key: 'ACTIVE', label: t('filterActive') },
      { key: 'COMPLETED', label: t('filterCompleted') },
      { key: 'DEFAULTED', label: t('filterDefaulted') },
      { key: 'CANCELLED', label: t('filterCancelled') },
    ],
    [t],
  );

  const viewTabs = useMemo(
    () => [
      { key: 'all', label: t('viewAllPlans') },
      { key: 'overdue', label: t('viewOverdue') },
    ],
    [t],
  );

  const paymentMethods = useMemo(
    () => [
      { value: 'CASH', label: t('payMethodCash') },
      { value: 'CARD', label: t('payMethodCard') },
      { value: 'BANK_TRANSFER', label: t('payMethodBankTransfer') },
      { value: 'MOBILE_WALLET', label: t('payMethodMobileWallet') },
    ],
    [t],
  );

  // Bank-settled methods credit a bank account (T-488). Bank transfer must name it; card / wallet may omit
  // it (the tenant's sole active account is used).
  const isBankSettled = (method: string) => ['CARD', 'BANK_TRANSFER', 'MOBILE_WALLET'].includes(method);

  const frequencyOptions = [
    { value: 'WEEKLY', label: t('freqWeekly') },
    { value: 'BIWEEKLY', label: t('freqBiweekly') },
    { value: 'MONTHLY', label: t('freqMonthly') },
  ];
  const frequencyLabel = (f: string) => frequencyOptions.find((o) => o.value === f)?.label ?? f;
  const paymentStatusLabels: Record<string, string> = {
    PENDING: t('payStatusPending'),
    PAID: t('payStatusPaid'),
    OVERDUE: t('payStatusOverdue'),
    WAIVED: t('payStatusWaived'),
  };

  const { data, isLoading } = useQuery<InstallmentListResponse>({
    queryKey: ['installment-plans', page, statusFilter],
    queryFn: () => api.get('/installments', { params: { page, limit: 20, status: statusFilter || undefined } }).then((r) => r.data),
    enabled: viewTab === 'all',
  });

  const { data: overdueData, isLoading: overdueLoading } = useQuery<InstallmentPlan[] | InstallmentListResponse>({
    queryKey: ['installment-plans-overdue'],
    queryFn: () => api.get('/installments/overdue').then((r) => r.data),
    enabled: viewTab === 'overdue',
  });

  const { data: planDetail, isLoading: detailLoading } = useQuery<InstallmentPlanDetail>({
    queryKey: ['installment-plan', detailPlan?.id],
    queryFn: () => api.get(`/installments/${detailPlan?.id}`).then((r) => r.data),
    enabled: !!detailPlan?.id,
  });

  const { data: bankAccounts = [] } = useQuery<{ id: string; name: string; bankName: string }[]>({
    queryKey: ['bank-accounts-installments'],
    queryFn: () => api.get('/bank-accounts').then((r) => r.data?.data ?? r.data ?? []),
  });

  const createMutation = useMutation({
    mutationFn: (d: CreatePlanInput) => api.post('/installments', d).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('toastPlanCreated'));
      setCreateOpen(false);
      setForm(emptyForm());
      setSelectedSale(null);
      qc.invalidateQueries({ queryKey: ['installment-plans'] });
    },
    onError: (err) => notify.error(err, t('errCreatePlan')),
  });

  const payMutation = useMutation({
    mutationFn: ({ planId, ...d }: PayInstallmentInput) => api.post(`/installments/${planId}/pay`, d).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('toastPaymentRecorded'));
      setPayOpen(false);
      setPayPlan(null);
      setPayForm(emptyPayForm());
      qc.invalidateQueries({ queryKey: ['installment-plans'] });
      qc.invalidateQueries({ queryKey: ['installment-plans-overdue'] });
      if (payPlan?.id) qc.invalidateQueries({ queryKey: ['installment-plan', payPlan.id] });
      if (detailPlan?.id) qc.invalidateQueries({ queryKey: ['installment-plan', detailPlan.id] });
    },
    onError: (err) => notify.error(err, t('errPaymentFailed')),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/installments/${id}/cancel`).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('toastPlanCancelled'));
      qc.invalidateQueries({ queryKey: ['installment-plans'] });
    },
    onError: (err) => notify.error(err, t('errCancelFailed')),
  });

  const openPayDialog = (plan: InstallmentPlan, installment: InstallmentPayment) => {
    setPayPlan(plan);
    setPayForm({ paymentId: installment.id, amount: Number(installment.amount), installmentDue: Number(installment.amount), paymentMethod: 'CASH', bankAccountId: '', notes: '' });
    setPayOpen(true);
  };

  const closePayDialog = () => {
    setPayOpen(false);
    setPayPlan(null);
    setPayForm(emptyPayForm());
  };

  const overduePlans = Array.isArray(overdueData) ? overdueData : overdueData?.data;
  const displayData: InstallmentPlan[] | undefined = viewTab === 'overdue' ? overduePlans : data?.data;
  const isLoadingDisplay = viewTab === 'overdue' ? overdueLoading : isLoading;

  // Create-dialog live preview: installments are financed from (sale total − down payment).
  const saleTotal = Number(selectedSale?.totalAmount ?? 0);
  const downNum = Number(form.downPayment) || 0;
  const financedAmount = saleTotal - downNum;
  const nInstallments = Number(form.numberOfInstallments) || 0;
  const perInstallment = nInstallments > 0 && financedAmount > 0 ? financedAmount / nInstallments : 0;
  const downExceedsTotal = !!selectedSale && saleTotal > 0 && downNum >= saleTotal;
  const createBankMissing = form.downPayment > 0 && form.downPaymentMethod === 'BANK_TRANSFER' && !form.downPaymentBankAccountId;

  // Pay-dialog derived guards/hints.
  const payRemaining = Number(payPlan?.remainingAmount ?? 0);
  const payExceedsRemaining = payRemaining > 0 && payForm.amount > payRemaining + 0.01;
  const payBankMissing = payForm.paymentMethod === 'BANK_TRANSFER' && !payForm.bankAccountId;
  const payCoversCount = (() => {
    if (!payPlan?.payments || payForm.amount <= 0) return 0;
    const target = payPlan.payments.find((p) => p.id === payForm.paymentId);
    if (!target) return 0;
    const ordered = [
      target,
      ...payPlan.payments.filter((p) => p.id !== target.id && p.status !== 'PAID' && p.status !== 'WAIVED'),
    ];
    let remaining = payForm.amount;
    let count = 0;
    for (const inst of ordered) {
      if (remaining <= 0.001) break;
      const needed = Number(inst.amount) - Number(inst.paidAmount ?? 0);
      if (needed <= 0) continue;
      if (remaining >= needed - 0.001) { remaining -= needed; count += 1; } else break;
    }
    return count;
  })();

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={t('pageTitle')}
        subtitle={t('pageSubtitle')}
        actions={canCreate ? (
          <Button onClick={() => setCreateOpen(true)} variant="gradient" className="gap-2">
            <Plus className="h-4 w-4" /> {t('newPlan')}
          </Button>
        ) : undefined}
      />

      <Card>
        <div className="border-b border-border">
          <TabBar tabs={viewTabs} activeTab={viewTab} onTabChange={(k) => setViewTab(k as ViewTab)} />
        </div>
        {viewTab === 'all' && (
          <div className="border-b border-border px-4 py-3">
            <FilterChips
              chips={statusFilterChips}
              value={statusFilter}
              onChange={(key) => { setStatusFilter(key); setPage(1); }}
            />
          </div>
        )}
        <CardContent className="pt-4">
          {isLoadingDisplay ? (
            <SkeletonTable rows={5} cols={7} />
          ) : !displayData?.length ? (
            <EmptyState
              icon={CreditCard}
              title={viewTab === 'overdue' ? t('emptyOverdueTitle') : t('emptyAllTitle')}
              description={viewTab === 'overdue' ? t('emptyOverdueDesc') : t('emptyAllDesc')}
              action={canCreate && viewTab === 'all' ? { label: t('newPlan'), onClick: () => setCreateOpen(true) } : undefined}
            />
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('tblInvoice')}</Th>
                    <Th>{t('tblCustomer')}</Th>
                    <Th className="text-end">{t('tblTotal')}</Th>
                    <Th className="text-end">{t('tblPaid')}</Th>
                    <Th className="text-end">{t('tblRemaining')}</Th>
                    <Th className="text-center">{t('tblProgress')}</Th>
                    <Th>{t('tblNextDue')}</Th>
                    <Th className="text-center">{t('tblStatus')}</Th>
                    <Th>{t('tblActions')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {displayData.map((plan) => {
                    const nextInstallment = plan.payments?.find((p) => p.status === 'PENDING' || p.status === 'OVERDUE');
                    return (
                      <Tr key={plan.id}>
                        <Td label={t('tblInvoice')} className="font-medium text-xs">
                          {plan.sale?.invoiceNumber ? <RecordLink type="invoice" id={plan.sale.id} label={plan.sale.invoiceNumber} variant="mono" className="text-xs" /> : '—'}
                        </Td>
                        <Td label={t('tblCustomer')}>{plan.customer?.name ?? '—'}</Td>
                        <Td label={t('tblTotal')} className="text-end font-mono">{formatCurrency(plan.totalAmount)}</Td>
                        <Td label={t('tblPaid')} className="text-end font-mono text-success-text">{formatCurrency(plan.amountPaid)}</Td>
                        <Td label={t('tblRemaining')} className="text-end font-mono text-warning-text">{formatCurrency(plan.remainingAmount)}</Td>
                        <Td label={t('tblProgress')} className="text-center">
                          <div className="flex items-center gap-1.5 justify-center">
                            <div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className="h-full bg-primary rounded-full transition-all"
                                style={{ width: `${(plan.paidInstallments / plan.numberOfInstallments) * 100}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground">{plan.paidInstallments}/{plan.numberOfInstallments}</span>
                          </div>
                        </Td>
                        <Td label={t('tblNextDue')} className="text-xs">
                          {plan.status === 'ACTIVE' ? (
                            <span className={nextInstallment?.status === 'OVERDUE' ? 'text-destructive-text font-medium' : ''}>
                              {formatDateOnly(plan.nextDueDate)}
                            </span>
                          ) : '—'}
                        </Td>
                        <Td label={t('tblStatus')} className="text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[plan.status] ?? ''}`}>
                            {planStatusLabel(plan.status)}
                          </span>
                        </Td>
                        <Td label={t('tblActions')}>
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" className="text-xs h-7"
                              onClick={() => { setDetailPlan(plan); }}>
                              View
                            </Button>
                            {canCreate && plan.status === 'ACTIVE' && nextInstallment && (
                              <Button variant="outline" size="sm" className="text-xs h-7 border-success text-success-text hover:bg-success/10"
                                onClick={() => openPayDialog(plan, nextInstallment)}>
                                Pay
                              </Button>
                            )}
                          </div>
                        </Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            </div>
          )}
          {viewTab === 'all' && (data?.total ?? 0) > 20 && (
            <div className="mt-4 border-t pt-4">
              <PaginationBar page={page} pageSize={20} total={data?.total ?? 0} onPageChange={setPage} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Plan Dialog */}
      <Dialog visible={createOpen} onHide={() => { setCreateOpen(false); setForm(emptyForm()); setSelectedSale(null); }}
        header={t('createPlanTitle')} style={{ width: '480px' }}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setCreateOpen(false); setForm(emptyForm()); setSelectedSale(null); }}>{t('cancelBtn')}</Button>
            <Button disabled={createMutation.isPending || !form.saleId || form.numberOfInstallments < 2 || downExceedsTotal || createBankMissing}
              onClick={() => createMutation.mutate({ ...form, numberOfInstallments: Number(form.numberOfInstallments), downPayment: Number(form.downPayment) })}>
              {createMutation.isPending ? t('creatingEllipsis') : t('createPlanBtn')}
            </Button>
          </div>
        }>
        <div className="space-y-3 pt-2">
          <div>
            <Label className="block mb-1">{t('saleInvoiceLabel')}</Label>
            <EntityAutocomplete<SaleOption>
              endpoint="/sales"
              value={selectedSale}
              onChange={(sale) => {
                setSelectedSale(sale);
                setForm({ ...form, saleId: sale?.id ?? '' });
              }}
              getLabel={(s) =>
                `${s.invoiceNumber}${s.customer?.name ? ` · ${s.customer.name}` : ''}`
              }
              itemTemplate={(s) => (
                <div className="flex flex-col py-0.5">
                  <span className="font-medium text-sm">{s.invoiceNumber}</span>
                  <span className="text-xs text-muted-foreground">
                    {(s.customer?.name ?? t('walkInCustomer'))} · {formatCurrency(Number(s.totalAmount))}
                  </span>
                </div>
              )}
              placeholder={t('searchInvoicePlaceholder')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1">{t('downPaymentLabel')}</Label>
              <Input type="number" min="0" step="1" value={form.downPayment}
                onChange={(e) => setForm({ ...form, downPayment: Number(e.target.value) })} />
            </div>
            <div>
              <Label className="block mb-1">{t('numInstallmentsLabel')}</Label>
              <Input type="number" min="2" max="60" step="1" value={form.numberOfInstallments}
                onChange={(e) => setForm({ ...form, numberOfInstallments: Number(e.target.value) })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1">{t('frequencyLabel')}</Label>
              <Dropdown value={form.frequency} options={frequencyOptions} optionLabel="label" optionValue="value" onChange={(e) => setForm({ ...form, frequency: e.value })}
                className="w-full" style={{ width: '100%' }} />
            </div>
            <div>
              <Label className="block mb-1">{t('startDateLabel')}</Label>
              <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
            </div>
          </div>
          {form.downPayment > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="block mb-1">{t('downPaymentMethodLabel')}</Label>
                <Dropdown value={form.downPaymentMethod} options={paymentMethods} optionLabel="label" optionValue="value"
                  onChange={(e) => setForm({ ...form, downPaymentMethod: e.value, downPaymentBankAccountId: '' })}
                  className="w-full" style={{ width: '100%' }} />
                <p className="text-xs text-muted-foreground mt-1">{t('chequeUseKhata')}</p>
              </div>
              {isBankSettled(form.downPaymentMethod) && (
                <div>
                  <Label className="block mb-1">{t('bankAccountLabel')}{form.downPaymentMethod === 'BANK_TRANSFER' ? ' *' : ''}</Label>
                  {bankAccounts.length > 0 ? (
                    <Dropdown value={form.downPaymentBankAccountId}
                      onChange={(e) => setForm({ ...form, downPaymentBankAccountId: e.value })}
                      options={[
                        { label: t('selectBankAccount'), value: '' },
                        ...bankAccounts.map((acc) => ({ label: `${acc.name} — ${acc.bankName}`, value: acc.id })),
                      ]}
                      optionLabel="label"
                      optionValue="value"
                      placeholder={t('selectBankAccount')}
                      className="w-full" />
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">{t('noBankAccountsConfigured')}</p>
                  )}
                </div>
              )}
            </div>
          )}
          {selectedSale && (
            downExceedsTotal ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive-text">
                {t('downExceedsTotal', { total: formatCurrency(saleTotal) })}
              </div>
            ) : (
              <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('saleTotalLabel')}</span>
                  <span className="font-mono">{formatCurrency(saleTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('downCollectedNow')}</span>
                  <span className="font-mono">{formatCurrency(downNum)}</span>
                </div>
                <div className="flex justify-between border-t pt-1 font-medium">
                  <span>{t('financedAmount')}</span>
                  <span className="font-mono">{formatCurrency(financedAmount)}</span>
                </div>
                <div className="flex justify-between text-primary-text">
                  <span>{t('installmentLine', { count: nInstallments })}</span>
                  <span className="font-mono">≈ {formatCurrency(perInstallment)}</span>
                </div>
              </div>
            )
          )}
          <div>
            <Label className="block mb-1">{t('notesLabel')}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} className="resize-none" />
          </div>
        </div>
      </Dialog>

      {/* Plan Detail Dialog */}
      <Dialog visible={!!detailPlan && !payOpen} onHide={() => setDetailPlan(null)}
        header={t('planDetailTitle', { invoice: detailPlan?.sale?.invoiceNumber ?? '' })} style={{ width: '600px' }}>
        {detailLoading ? (
          <SkeletonTable rows={4} cols={4} />
        ) : planDetail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">{t('sumTotal')}</div>
                <div className="font-mono font-semibold">{formatCurrency(planDetail.totalAmount)}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">{t('sumPaid')}</div>
                <div className="font-mono font-semibold text-success-text">{formatCurrency(planDetail.amountPaid)}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">{t('sumRemaining')}</div>
                <div className="font-mono font-semibold text-warning-text">{formatCurrency(planDetail.remainingAmount)}</div>
              </div>
            </div>
            {Number(planDetail.downPayment) > 0 && (
              <div className="flex items-center justify-between rounded-lg border bg-muted/40 p-3 text-sm">
                <span className="text-muted-foreground">{t('downCollectedAtPurchase')}</span>
                <span className="font-mono font-semibold text-success-text">{formatCurrency(Number(planDetail.downPayment))}</span>
              </div>
            )}
            <div className="text-sm text-muted-foreground">
              {t('planMetaLine', { frequency: frequencyLabel(planDetail.frequency), count: planDetail.numberOfInstallments, paid: planDetail.paidInstallments })}
            </div>
            <div className="overflow-x-auto rounded-lg border">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>#</Th>
                    <Th>{t('tblDueDate')}</Th>
                    <Th className="text-end">{t('tblAmount')}</Th>
                    <Th className="text-center">{t('tblStatus')}</Th>
                    <Th>{t('tblPaidAt')}</Th>
                    {canCreate && <Th />}
                  </Tr>
                </THead>
                <TBody>
                  {planDetail.payments.map((pmt, i) => (
                    <Tr key={pmt.id}>
                      <Td label="#" className="text-muted-foreground">{i + 1}</Td>
                      <Td label={t('tblDueDate')}>{formatDate(pmt.dueDate)}</Td>
                      <Td label={t('tblAmount')} className="text-end font-mono">{formatCurrency(pmt.amount)}</Td>
                      <Td label={t('tblStatus')} className="text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PAYMENT_STATUS_COLORS[pmt.status] ?? ''}`}>
                          {paymentStatusLabels[pmt.status] ?? pmt.status}
                        </span>
                      </Td>
                      <Td label={t('tblPaidAt')} className="text-xs text-muted-foreground">{pmt.paidAt ? formatDate(pmt.paidAt) : '—'}</Td>
                      {canCreate && (
                        <Td>
                          {(pmt.status === 'PENDING' || pmt.status === 'OVERDUE') && planDetail.status === 'ACTIVE' && (
                            <Button variant="ghost" size="sm" className="text-xs h-6 text-success-text"
                              onClick={() => openPayDialog(planDetail, pmt)}>
                              <CheckCircle className="h-3 w-3 me-1" /> {t('payAction')}
                            </Button>
                          )}
                        </Td>
                      )}
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </div>
            {canCancel && planDetail.status === 'ACTIVE' && (
              <div className="flex justify-end pt-2">
                <Button variant="destructive" size="sm" onClick={() => setConfirmCancelId(planDetail.id)}>
                  {t('cancelPlanBtn')}
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog visible={payOpen} onHide={closePayDialog}
        header={t('recordPaymentTitle')} style={{ width: '400px' }}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={closePayDialog}>{t('cancelBtn')}</Button>
            <Button disabled={payMutation.isPending || payForm.amount <= 0 || payExceedsRemaining || payBankMissing}
              onClick={() => payMutation.mutate({ planId: payPlan?.id, ...payForm })}>
              {payMutation.isPending ? t('savingEllipsis') : t('recordPaymentBtn')}
            </Button>
          </div>
        }>
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-2 p-3 rounded-lg bg-info/10 text-info-text text-sm">
            <Calendar className="h-4 w-4 shrink-0" />
            <span>{t('installmentDue')}: <strong>{formatCurrency(payForm.installmentDue)}</strong>{payRemaining > 0 && <> · {t('planRemaining')}: <strong>{formatCurrency(payRemaining)}</strong></>}</span>
          </div>
          <div>
            <Label className="block mb-1">{t('amountReceivedLabel')}</Label>
            <Input type="number" min="0.01" step="0.01" value={payForm.amount}
              onChange={(e) => setPayForm({ ...payForm, amount: Number(e.target.value) })} />
            {payExceedsRemaining ? (
              <p className="text-xs text-destructive-text mt-1">{t('amountExceedsRemaining', { amount: formatCurrency(payRemaining) })}</p>
            ) : payCoversCount > 1 ? (
              <p className="text-xs text-success-text mt-1">{t('coversInstallments', { count: payCoversCount })}</p>
            ) : null}
          </div>
          <div>
            <Label className="block mb-1">{t('paymentMethodLabel')}</Label>
            <Dropdown value={payForm.paymentMethod} options={paymentMethods} optionLabel="label" optionValue="value"
              onChange={(e) => setPayForm({ ...payForm, paymentMethod: e.value, bankAccountId: '' })}
              className="w-full" style={{ width: '100%' }} />
            <p className="text-xs text-muted-foreground mt-1">{t('chequeUseKhata')}</p>
          </div>
          {isBankSettled(payForm.paymentMethod) && (
            <div>
              <Label className="block mb-1">{t('bankAccountLabel')}{payForm.paymentMethod === 'BANK_TRANSFER' ? ' *' : ''}</Label>
              {bankAccounts.length > 0 ? (
                <Dropdown value={payForm.bankAccountId}
                  onChange={(e) => setPayForm({ ...payForm, bankAccountId: e.value })}
                  options={[
                    { label: t('selectBankAccount'), value: '' },
                    ...bankAccounts.map((acc) => ({ label: `${acc.name} — ${acc.bankName}`, value: acc.id })),
                  ]}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={t('selectBankAccount')}
                  className="w-full" />
              ) : (
                <p className="text-xs text-muted-foreground mt-1">{t('noBankAccountsAddOne')}</p>
              )}
            </div>
          )}
          <div>
            <Label className="block mb-1">{t('notesLabel')}</Label>
            <Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} placeholder={t('optionalPlaceholder')} />
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!confirmCancelId}
        onClose={() => setConfirmCancelId(null)}
        onConfirm={() => {
          if (confirmCancelId) {
            cancelMutation.mutate(confirmCancelId);
            setDetailPlan(null);
          }
          setConfirmCancelId(null);
        }}
        title={t('cancelPlanTitle')}
        description={t('cancelPlanDescription')}
        confirmLabel={t('cancelPlanConfirm')}
        variant="danger"
      />
    </div>
  );
}
